<div
  class="flex flex-col scrollbar"
  [class]="embedded() ? 'flex-1 min-h-0' : 'h-[calc(100vh-2rem)]'"
>
  @if (loading) {
    <maximizer-loader type="legacy"></maximizer-loader>
  }

  <form
    class="flex flex-1 flex-col p-4 text-caption gap-2"
    [formGroup]="form.group"
  >
    <kendo-formfield>
      <kendo-label
        [for]="subject"
        [text]="'outlook.appointment.subject' | translate"
      >
        <span [class.text-error]="form.controls.subject.errorVisible">*</span>
      </kendo-label>
      <kendo-textbox
        formControlName="subject"
        #subject
        [maxlength]="subjectMaxLength"
        required
      ></kendo-textbox>
      <kendo-formerror>{{
        'outlook.forms.requiredField' | translate
      }}</kendo-formerror>
    </kendo-formfield>

    <kendo-formfield>
      <kendo-label [for]="date" [text]="'outlook.appointment.date' | translate">
        <span [class.text-error]="form.controls.date.errorVisible">*</span>
      </kendo-label>
      <div class="flex items-center gap-2">
        <kendo-datepicker
          #date
          class="flex-1"
          [format]="dateFormat"
          formControlName="date"
          required
          [navigation]="false"
          calendarType="classic"
          (valueChange)="onDateChange()"
        >
        </kendo-datepicker>
        <button
          kendoButton
          type="button"
          id="outlook-appointment-ui-addAppointment-repeat"
          class="w-9 h-9 shrink-0 k-toggle-button"
          [class.k-selected]="!!recurrence()"
          iconClass="fa-icons icon-solid icon-16 icon-rotate-right"
          [attr.aria-label]="
            'outlook.appointment.recurrence.repeat' | translate
          "
          [title]="'outlook.appointment.recurrence.repeat' | translate"
          [disabled]="!form.controls.date.value"
          (click)="openRepeatDialog()"
        ></button>
      </div>
      <kendo-formerror>{{
        'outlook.forms.requiredField' | translate
      }}</kendo-formerror>
    </kendo-formfield>

    @if (recurrence()) {
      <div class="flex items-center gap-2">
        <span
          class="fa-icons icon-solid icon-14 icon-rotate-right text-forest-green-100"
        ></span>
        <span class="text-body text-forest-green-100 truncate">{{
          recurrenceSummary()
        }}</span>
        <button
          kendoButton
          type="button"
          id="outlook-appointment-ui-addAppointment-clearRepeat"
          themeColor="primary"
          fillMode="flat"
          iconClass="fa-icons icon-xmark icon-14"
          class="ml-auto shrink-0"
          [attr.aria-label]="'outlook.appointment.recurrence.clear' | translate"
          [title]="'outlook.appointment.recurrence.clear' | translate"
          (click)="clearRecurrence()"
        ></button>
      </div>
      @if (recurrenceTruncationNote(); as note) {
        <p class="caption text-neutral-60">{{ note }}</p>
      }
    }

    <div class="flex gap-5 py-2">
      <div class="flex w-1/2 gap-2 items-center">
        <span class="fa-icons icon-clock icon-14"></span>
        <kendo-label
          [for]="allDaySwitch"
          [text]="'outlook.appointment.allDay' | translate"
          class="text-body"
        ></kendo-label>
        <kendo-switch
          #allDaySwitch
          id="outlook-appointment-ui-addAppointment-allDaySwitch"
          formControlName="isAllDay"
          size="small"
          [title]="'outlook.appointment.allDay' | translate"
          (valueChange)="onAllDayChange($event)"
        ></kendo-switch>
      </div>
      <div class="flex w-1/2 gap-2 items-center">
        <span class="fa-icons icon-solid icon-lock icon-14"></span>
        <kendo-label
          [for]="privateSwitch"
          [text]="'outlook.appointment.private' | translate"
          class="text-body"
        ></kendo-label>
        <kendo-switch
          #privateSwitch
          id="outlook-appointment-ui-addAppointment-privateSwitch"
          formControlName="isPrivate"
          size="small"
          [title]="'outlook.appointment.private' | translate"
        ></kendo-switch>
      </div>
    </div>

    <div class="flex flex-row justify-between space-x-2">
      <kendo-formfield class="w-2/4">
        <kendo-label
          [for]="startTime"
          [text]="'outlook.appointment.startTime' | translate"
        >
          <span [class.text-error]="form.controls.startTime.errorVisible"
            >*</span
          >
        </kendo-label>
        <kendo-timepicker
          #startTime
          [format]="outlookStore.timeFormatShort()"
          formControlName="startTime"
          required
          (valueChange)="onStartChange()"
        >
        </kendo-timepicker>
        <kendo-formerror>{{
          'outlook.forms.requiredField' | translate
        }}</kendo-formerror>
      </kendo-formfield>
      <span class="my-auto pt-4">-</span>
      <kendo-formfield class="w-2/4">
        <kendo-label
          [for]="endTime"
          [text]="'outlook.appointment.endTime' | translate"
        >
          <span [class.text-error]="form.controls.endTime.errorVisible">*</span>
        </kendo-label>
        <kendo-timepicker
          #endTime
          [format]="outlookStore.timeFormatShort()"
          formControlName="endTime"
          required
          [min]="form.controls.startTime.value"
        >
        </kendo-timepicker>
        <kendo-formerror>
          @if (form.controls.endTime.hasError('timeRangeError')) {
            {{ 'outlook.appointment.endTimeBeforeStart' | translate }}
          } @else {
            {{ 'outlook.forms.requiredField' | translate }}
          }
        </kendo-formerror>
      </kendo-formfield>
    </div>

    <kendo-formfield>
      <kendo-label
        [for]="location"
        [text]="'outlook.appointment.location' | translate"
      >
      </kendo-label>

      <kendo-combobox
        [data]="configuration.options.locations"
        textField="name"
        valueField="id"
        [allowCustom]="true"
        [valuePrimitive]="true"
        formControlName="location"
        #location
      >
      </kendo-combobox>
    </kendo-formfield>

    <kendo-formfield>
      <kendo-label
        [for]="primaryContact"
        [text]="'outlook.appointment.assignedUser' | translate"
      >
        <span [class.text-error]="form.controls.assignedUser.errorVisible"
          >*</span
        >
      </kendo-label>

      <kendo-dropdownlist
        [data]="configuration.users"
        textField="name"
        valueField="key"
        fillMode="solid"
        [valuePrimitive]="true"
        formControlName="assignedUser"
        #primaryContact
      >
      </kendo-dropdownlist>
    </kendo-formfield>

    <kendo-formfield>
      <kendo-label [text]="'outlook.appointment.attendees' | translate">
        <span [class.text-error]="form.controls.attendees.errorVisible">*</span>
      </kendo-label>
      <kendo-multiselect
        class="appointment-multiselect"
        textField="name"
        valueField="id"
        [filterable]="true"
        [data]="filterableAttendeeList"
        formControlName="attendees"
        [clearButton]="false"
        (filterChange)="handleAttendeesAutoComplete($event)"
        (removeTag)="handleRemoveAttendees($event)"
        (blur)="resetAttendeesToSelected()"
        (open)="resetAttendeesToSelected()"
        [loading]="autocompleteLoading"
        [itemDisabled]="disableAttendees"
      >
        @if (autocompleteLoading) {
          <ng-template kendoMultiSelectNoDataTemplate>
            <maximizer-loader type="legacy"></maximizer-loader>
          </ng-template>
        }

        <ng-template
          kendoMultiSelectTagTemplate
          let-dataItem
          kendoMultiSelectCustomItemTemplate
        >
          <span
            [class]="'fa-icons icon icon-solid icon-12 pr-1 ' + dataItem.icon"
          ></span>
          {{ dataItem.name }}
        </ng-template>

        <ng-template kendoMultiSelectItemTemplate let-dataItem>
          <span
            [class]="'fa-icons icon icon-solid icon-12 ' + dataItem.icon"
          ></span>
          {{ dataItem.name }}
        </ng-template>
      </kendo-multiselect>

      <kendo-formerror>{{
        'outlook.forms.requiredField' | translate
      }}</kendo-formerror>
    </kendo-formfield>

    <kendo-formfield>
      <kendo-label [text]="'outlook.appointment.description' | translate">
      </kendo-label>
      <kendo-editor
        formControlName="description"
        [iframe]="false"
        [pasteCleanupSettings]="descriptionPasteCleanup"
        [style.height.px]="descriptionEditorHeight"
        class="min-w-full max-w-full"
      >
        <kendo-toolbar [overflow]="true" [popupSettings]="{ appendTo: view }">
          <kendo-toolbar-buttongroup>
            <kendo-toolbar-button kendoEditorBoldButton></kendo-toolbar-button>
            <kendo-toolbar-button
              kendoEditorItalicButton
            ></kendo-toolbar-button>
            <kendo-toolbar-button
              kendoEditorUnderlineButton
            ></kendo-toolbar-button>
          </kendo-toolbar-buttongroup>
          <kendo-toolbar-buttongroup>
            <kendo-toolbar-button
              kendoEditorInsertUnorderedListButton
            ></kendo-toolbar-button>
            <kendo-toolbar-button
              kendoEditorInsertOrderedListButton
            ></kendo-toolbar-button>
          </kendo-toolbar-buttongroup>
          <kendo-toolbar-button
            kendoEditorCreateLinkButton
          ></kendo-toolbar-button>
          <kendo-toolbar-button kendoEditorUnlinkButton></kendo-toolbar-button>
        </kendo-toolbar>
      </kendo-editor>
    </kendo-formfield>

    <kendo-formfield>
      <kendo-label
        [text]="'outlook.appointment.category' | translate"
      ></kendo-label>
      <kendo-dropdownlist
        [data]="configuration.options.categories"
        textField="name"
        valueField="id"
        fillMode="solid"
        formControlName="category"
        [defaultItem]="{ id: '', name: '' }"
        [valuePrimitive]="true"
      >
      </kendo-dropdownlist>
    </kendo-formfield>

    <kendo-formfield>
      <kendo-label
        [text]="'outlook.appointment.result' | translate"
      ></kendo-label>
      <kendo-dropdownlist
        [data]="configuration.options.results"
        textField="name"
        valueField="id"
        fillMode="solid"
        formControlName="result"
        [defaultItem]="{ id: '', name: '' }"
        [valuePrimitive]="true"
      >
      </kendo-dropdownlist>
    </kendo-formfield>
  </form>

  <div
    class="bg-neutral-5 z-40 mt-auto flex justify-end gap-2 p-2 bottom-0 sticky"
  >
    @if (saveProgressParams(); as progressParams) {
      <span class="text-caption text-neutral-90 my-auto mr-auto">
        {{
          'outlook.appointment.recurrence.saving' | translate: progressParams
        }}
      </span>
    }
    <button
      kendoButton
      id="outlook-appointment-ui-addAppointment-cancel"
      class="uppercase"
      themeColor="primary"
      fillMode="flat"
      (click)="cancel()"
      [disabled]="loading"
    >
      {{ 'outlook.buttons.cancel' | translate }}
    </button>
    <button
      kendoButton
      id="outlook-appointment-ui-addAppointment-save"
      class="uppercase"
      themeColor="primary"
      [disabled]="!form.valid || loading || multiDayPrefill()"
      (click)="save()"
    >
      {{ 'outlook.buttons.save' | translate }}
    </button>
  </div>
</div>

@if (repeatDialogOpen()) {
  <maximizer-appointment-repeat-dialog
    [value]="recurrence()"
    [startsOn]="form.controls.date.value"
    (saved)="onRepeatSaved($event)"
    (cancelled)="onRepeatCancelled()"
  ></maximizer-appointment-repeat-dialog>
}

<maximizer-outlook-notification
  #notification
  [closable]="true"
  entryId="appointment"
>
</maximizer-outlook-notification>
